// GTM Tracking Utilities
// Use these functions to track custom events throughout your Vue app

/**
 * Get user session data for enhanced tracking
 */
function getSessionData() {
  return {
    session_id: sessionStorage.getItem('session_id') || Date.now().toString(),
    user_agent: navigator.userAgent,
    screen_resolution: `${screen.width}x${screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    device_type: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop',
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer || 'direct',
    timestamp: new Date().toISOString()
  }
}

/**
 * Track a page view with enhanced data
 * @param {string} page - Page name/path
 * @param {string} title - Page title
 * @param {Object} additionalData - Additional page-specific data
 */
export function trackPageView(page, title, additionalData = {}) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'page_view',
      page_location: window.location.href,
      page_path: page,
      page_title: title,
      ...getSessionData(),
      ...additionalData
    })
  }
}

/**
 * Track a video interaction with enhanced metrics
 * @param {string} action - Action type (play, pause, complete, etc.)
 * @param {string} videoTitle - Title of the video
 * @param {string} videoId - YouTube video ID
 * @param {Object} videoData - Additional video data
 */
export function trackVideoEvent(action, videoTitle, videoId, videoData = {}) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'video_interaction',
      video_action: action,
      video_title: videoTitle,
      video_id: videoId,
      video_provider: 'youtube',
      video_url: `https://www.youtube.com/watch?v=${videoId}`,
      video_duration: videoData.duration || null,
      video_current_time: videoData.currentTime || 0,
      video_percent_watched: videoData.percentWatched || 0,
      video_quality: videoData.quality || null,
      engagement_time: videoData.engagementTime || 0,
      ...getSessionData()
    })
  }
}

/**
 * Track scroll depth for engagement measurement
 * @param {number} scrollPercent - Percentage of page scrolled
 * @param {string} pagePath - Current page path
 */
export function trackScrollDepth(scrollPercent, pagePath) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'scroll_depth',
      scroll_percent: scrollPercent,
      page_path: pagePath,
      scroll_depth_threshold: Math.floor(scrollPercent / 25) * 25, // 0, 25, 50, 75, 100
      ...getSessionData()
    })
  }
}

/**
 * Track time spent on page/section
 * @param {string} section - Section name or page
 * @param {number} timeSpent - Time in seconds
 * @param {string} action - 'start' or 'end'
 */
export function trackTimeEngagement(section, timeSpent, action = 'end') {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'time_engagement',
      engagement_section: section,
      engagement_time_seconds: timeSpent,
      engagement_action: action,
      engagement_quality: timeSpent > 30 ? 'high' : timeSpent > 10 ? 'medium' : 'low',
      ...getSessionData()
    })
  }
}

/**
 * Track search interactions
 * @param {string} searchTerm - What user searched for
 * @param {number} resultCount - Number of results returned
 * @param {string} searchType - Type of search (videos, projects, etc.)
 */
export function trackSearch(searchTerm, resultCount, searchType = 'general') {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'search_interaction',
      search_term: searchTerm,
      search_results_count: resultCount,
      search_type: searchType,
      search_success: resultCount > 0,
      ...getSessionData()
    })
  }
}

/**
 * Track form interactions
 * @param {string} formName - Name/ID of the form
 * @param {string} action - 'start', 'submit', 'error', 'abandon'
 * @param {Object} formData - Form field data (anonymized)
 */
export function trackFormInteraction(formName, action, formData = {}) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'form_interaction',
      form_name: formName,
      form_action: action,
      form_fields_filled: formData.fieldsCount || 0,
      form_completion_time: formData.completionTime || 0,
      form_errors: formData.errors || [],
      ...getSessionData()
    })
  }
}

/**
 * Track user errors and issues
 * @param {string} errorType - Type of error (404, api_failure, js_error, etc.)
 * @param {string} errorMessage - Error message or description
 * @param {string} location - Where the error occurred
 */
export function trackError(errorType, errorMessage, location) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'error_tracking',
      error_type: errorType,
      error_message: errorMessage,
      error_location: location,
      error_timestamp: new Date().toISOString(),
      ...getSessionData()
    })
  }
}

/**
 * Track performance metrics
 * @param {Object} performanceData - Performance timing data
 */
export function trackPerformance(performanceData) {
  if (window.dataLayer && window.performance) {
    const navigation = performance.getEntriesByType('navigation')[0]
    
    window.dataLayer.push({
      event: 'performance_tracking',
      page_load_time: navigation?.loadEventEnd - navigation?.navigationStart || 0,
      dom_ready_time: navigation?.domContentLoadedEventEnd - navigation?.navigationStart || 0,
      first_contentful_paint: performanceData.fcp || null,
      largest_contentful_paint: performanceData.lcp || null,
      cumulative_layout_shift: performanceData.cls || null,
      first_input_delay: performanceData.fid || null,
      connection_type: navigator.connection?.effectiveType || 'unknown',
      ...getSessionData()
    })
  }
}

/**
 * Track file downloads
 * @param {string} fileName - Name of downloaded file
 * @param {string} fileType - Type/extension of file
 * @param {string} downloadSource - Where download was initiated
 */
export function trackDownload(fileName, fileType, downloadSource) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'file_download',
      file_name: fileName,
      file_type: fileType,
      download_source: downloadSource,
      download_url: window.location.href,
      ...getSessionData()
    })
  }
}

/**
 * Track a project interaction with enhanced data
 * @param {string} action - Action type (view, click, etc.)
 * @param {string} projectTitle - Title of the project
 * @param {string} projectLink - Project link/URL
 * @param {Object} projectData - Additional project data
 */
export function trackProjectEvent(action, projectTitle, projectLink, projectData = {}) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'project_interaction',
      project_action: action,
      project_title: projectTitle,
      project_link: projectLink,
      project_category: projectData.category || 'general',
      project_tags: projectData.tags || [],
      project_difficulty: projectData.difficulty || null,
      project_completion_status: projectData.status || null,
      interaction_context: projectData.context || 'card_click',
      ...getSessionData()
    })
  }
}

/**
 * Track navigation events with enhanced context
 * @param {string} linkText - Text of the navigation link
 * @param {string} destination - Destination URL/path
 * @param {Object} navData - Additional navigation data
 */
export function trackNavigation(linkText, destination, navData = {}) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'navigation_click',
      link_text: linkText,
      link_destination: destination,
      navigation_type: navData.type || 'header', // header, footer, sidebar, inline
      navigation_position: navData.position || null,
      previous_page: navData.fromPage || document.referrer,
      navigation_method: navData.method || 'click', // click, keyboard, swipe
      ...getSessionData()
    })
  }
}

/**
 * Track external link clicks with enhanced data
 * @param {string} linkText - Text of the link
 * @param {string} url - External URL
 * @param {Object} linkData - Additional link data
 */
export function trackExternalLink(linkText, url, linkData = {}) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'external_link_click',
      link_text: linkText,
      external_url: url,
      link_domain: new URL(url).hostname,
      link_context: linkData.context || 'content',
      link_position: linkData.position || null,
      opens_new_tab: linkData.newTab !== false,
      ...getSessionData()
    })
  }
}

/**
 * Track social media interactions with detailed metrics
 * @param {string} platform - Social platform (youtube, twitter, etc.)
 * @param {string} action - Action type (click, share, etc.)
 * @param {Object} socialData - Additional social data
 */
export function trackSocialEvent(platform, action, socialData = {}) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'social_interaction',
      social_platform: platform,
      social_action: action,
      social_content_type: socialData.contentType || 'link',
      social_content_id: socialData.contentId || null,
      social_content_title: socialData.contentTitle || null,
      social_button_location: socialData.location || 'header',
      ...getSessionData()
    })
  }
}

/**
 * Track language changes
 * @param {string} fromLanguage - Previous language
 * @param {string} toLanguage - New language
 * @param {string} method - How language was changed
 */
export function trackLanguageChange(fromLanguage, toLanguage, method = 'dropdown') {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'language_change',
      language_from: fromLanguage,
      language_to: toLanguage,
      language_change_method: method,
      page_when_changed: window.location.pathname,
      ...getSessionData()
    })
  }
}

/**
 * Track API call performance and errors
 * @param {string} apiEndpoint - API endpoint called
 * @param {string} status - success, error, timeout
 * @param {Object} apiData - API call data
 */
export function trackAPICall(apiEndpoint, status, apiData = {}) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'api_interaction',
      api_endpoint: apiEndpoint,
      api_status: status,
      api_response_time: apiData.responseTime || null,
      api_error_code: apiData.errorCode || null,
      api_error_message: apiData.errorMessage || null,
      api_retry_count: apiData.retryCount || 0,
      ...getSessionData()
    })
  }
}

/**
 * Track user preferences and settings
 * @param {string} setting - Setting name
 * @param {string} value - Setting value
 * @param {string} action - 'change', 'reset', 'save'
 */
export function trackUserPreference(setting, value, action = 'change') {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'user_preference',
      preference_name: setting,
      preference_value: value,
      preference_action: action,
      preference_category: setting.split('_')[0] || 'general',
      ...getSessionData()
    })
  }
}
